import { useState } from 'react';
import { foldLabel } from '../lib/format.js';
import { useNow } from '../lib/fresh.js';
import { api, ApiError, type DailyQuote, type DayFace } from '../lib/api.js';
import { Door } from './Door.js';
import { TodayDoses } from './TodayDoses.js';

/**
 * The night face, and the close. Release 3 of 2.0 (docs/PRD-2.0.md §2).
 *
 * The journal first, because that is when he talks; the slider stays above
 * this as the bypass; the night dose; a second quote; and Close the day.
 * Closing writes the day's line (at the rungs that write one) and marks the
 * day closed; the face then shows it as done, with Reopen until 4am. Nothing
 * is lost by not closing — the worker closes an unclosed day with what it
 * had, as it always did — and the copy says so.
 */
export function FaceNight({
  day,
  dayName,
  face,
  quote,
  onFace,
  onSaved,
  onBack,
}: {
  day: string;
  /** "Thursday" — the button says which day it is closing. */
  dayName: string;
  face: DayFace | null;
  quote: DailyQuote | null;
  /** The fixes pass: the seam back to the Now face, for a night face reached early. */
  onBack?: () => void;
  onFace: (f: DayFace) => void;
  /** Something was written; Today's lists should refresh. */
  onSaved: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closed = face?.closedAt ?? null;
  const now = useNow();

  async function close(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      onFace(await api.closeDay(day));
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not close the day.');
    } finally {
      setBusy(false);
    }
  }
  async function reopen(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      onFace(await api.reopenDay(day));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reopen it.');
    } finally {
      setBusy(false);
    }
  }

  if (closed) {
    return (
      <div className="stack-lg stack face-night">
        <section className="card stack-sm stack">
          <h2 className="face-h">{dayName} is closed</h2>
          <p className="muted" style={{ margin: 0 }}>
            Closed at {clock(closed)}. Its line, if one is written, is under “{foldLabel(day, now.toLocaleDateString('en-CA'))}” below.
            Until 4am you can reopen it.
          </p>
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void reopen()}>
            Reopen {dayName}
          </button>
          {error && (
            <p className="faint" role="alert">
              {error}
            </p>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className="stack-lg stack face-night">
      <section className="stack-sm stack">
        {/* Before 4am the night is yesterday's, and the heading says whose (14 Oct 2026). */}
        <h2 className="face-h">{day !== new Date().toLocaleDateString('en-CA') ? `How was ${dayName}?` : 'How was today?'}</h2>
        <Door day={day} prompt="Talk about the day…" autoOpen onSaved={onSaved} onFiled={onSaved} />
      </section>
      <TodayDoses />
      {quote && (
        <blockquote className="quote-line">
          <p className="reading">{quote.text}</p>
          <footer className="faint">— {quote.author}</footer>
        </blockquote>
      )}
      <button type="button" className="btn btn-primary btn-lg btn-block" disabled={busy} onClick={() => void close()}>
        {busy ? <span className="spinner" /> : `Close ${dayName}`}
      </button>
      {error && (
        <p className="faint" role="alert">
          {error}
        </p>
      )}
      <p className="faint">
        Closing writes the day's line and files it. You can reopen it until 4am. Nothing is lost by not
        closing: a day you leave open closes itself, with what it had.
      </p>
      {onBack && (
        <div className="seam">
          <button type="button" className="linkish" onClick={onBack}>
            ‹ Back to the day
          </button>
          <span className="faint">the slider and the day so far</span>
        </div>
      )}
    </div>
  );
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
